import { savedChange, savedRuleRequires, type ApprovalEvidence, type EvidenceChange } from './ApprovalEvidenceModel';
import { Alert, Badge, InfoTip } from '@zarya/ui';
import { t } from '@app/i18n';
import { ToolName } from '@app/shared/ToolName';
import {
  change,
  changeDetail,
  isToolChange,
  notCatalogedTip,
  severity as severityBadge,
  kindChange,
  pendencyChange,
  changedCode,
  type ApprovalDetail,
  type DiffChange,
} from './ApprovalsModel';
import s from './Approvals.module.css';

/**
 * O coração de T10 (FR-060): o diff de capabilities contra o contrato vigente
 * v{N}, com os 8 tipos de mudança, a severidade de cada uma e o rodapé
 * "Mudança de código · Mudança de autoridade · Severidade agregada".
 *
 * `nao_catalogado` **domina**: a linha ganha destaque e o ⓘ que diz o que fazer.
 *
 * As duas primeiras células do rodapé saem de `mudancaDaPendencia`/`mudouOCodigo`
 * desde 30/08 — a mesma fonte do alerta acima da dobra. Antes «Mudança de código»
 * era fixo em SIM (premissa do protótipo: release só de código não chegava aqui) e
 * «Mudança de autoridade» lia a classificação sozinha, que chama redução de
 * `CODE_CHANGE`: o rodapé dizia NÃO logo abaixo de um diff de capabilities
 * removidas.
 */
export function DiffTable({ detalhe }: { detalhe: ApprovalDetail }) {
  const diff = detalhe.diff;
  const base = detalhe.base_contract_version;
  const titulo = base
    ? t('approvals.diff.titleWithBase', { version: base })
    : t('approvals.diff.title');

  if (detalhe.diff_unavailable || !diff) {
    return (
      <section className={s.secao}>
        <h3 className={s.secaoTitulo}>{titulo}</h3>
        <Alert tone="warning" title={t('approvals.diff.unavailable.title')}>
          {t('approvals.diff.unavailable.body')}
        </Alert>
      </section>
    );
  }

  const mudancas = diff.changes ?? [];
  const readValue = change(pendencyChange(detalhe));
  const codigo = changedCode(detalhe);

  return (
    <section className={s.secao}>
      <h3 className={s.secaoTitulo}>{titulo}</h3>

      {diff.first_release ? (
        <Alert tone="info" title={t('approvals.diff.firstRelease.title')}>
          {t('approvals.diff.firstRelease.body')}
        </Alert>
      ) : null}

      {diff.catalog_available === false ? (
        <Alert tone="warning" title={t('approvals.diff.catalogUnavailable.title')}>
          {t('approvals.diff.catalogUnavailable.body')}
        </Alert>
      ) : null}

      {mudancas.length ? (
        <div className={s.diff}>
          {mudancas.map((mudanca, i) => (
            <RowDiff mudanca={mudanca} key={`${mudanca.kind}-${mudanca.capability}-${i}`}
              evidence={detalhe.decision_evidence}
              recorded={savedChange(detalhe.decision_evidence, i, mudanca.capability)} />
          ))}
        </div>
      ) : (
        // «release segue sem nova decisão» saiu em 30/08: a frase aparecia numa
        // pendência que está, justamente, parada esperando decisão.
        <p className={s.muted}>{readValue.semMudancas}</p>
      )}

      {diff.detail ? <p className={s.carregando}>{diff.detail}</p> : null}

      <div className={s.rodapeDiff}>
        <span className={s.rodapeItem}>
          {t('approvals.diff.footer.codeChange')}{' '}
          <b className={codigo ? s.sim : s.nao}>
            {t(codigo ? 'approvals.diff.footer.yes' : 'approvals.diff.footer.no')}
          </b>
        </span>
        <span className={s.rodapeItem}>
          {t('approvals.diff.footer.authorityChange')}{' '}
          <b className={authorityCor(readValue)}>{readValue.autoridade}</b>
        </span>
        <span className={s.rodapeItem}>
          {t('approvals.diff.footer.aggregatedSeverity')}{' '}
          <Badge tone={severityBadge(diff.severity ?? 'nenhuma').tone}>
            {severityBadge(diff.severity ?? 'nenhuma').rotulo}
          </Badge>
        </span>
      </div>
    </section>
  );
}

/**
 * A cor do «Mudança de autoridade».
 *
 * `null` é o `diff_indisponivel`: nem vermelho nem verde, porque nenhuma das duas
 * afirmações foi feita. Pintá-lo de verde diria "nada mudou" sobre o que ninguém
 * verificou — é a mesma escolha do tri-estado de `em_execucao`.
 */
function authorityCor(readValue: { autoridadeSim: boolean | null }): string {
  if (readValue.autoridadeSim === null) return s.muted;
  return readValue.autoridadeSim ? s.sim : s.nao;
}

function RowDiff({ mudanca, evidence, recorded }: {
  mudanca: DiffChange; evidence?: ApprovalEvidence; recorded?: EvidenceChange;
}) {
  const { rotulo, sinal } = kindChange(mudanca.kind);
  const severidade = severityBadge(mudanca.severity);
  const destaque = mudanca.severity === 'nao_catalogado';
  const cor = sinal === '+' ? s.add : sinal === '~' ? s.chg : s.del;

  const detalhe = changeDetail(mudanca);

  return (
    <div
      className={[s.linhaDiff, cor, destaque ? s.destaque : ''].filter(Boolean).join(' ')}
      data-tipo={mudanca.kind}
    >
      <span className={s.sinal} aria-hidden="true">
        {sinal}
      </span>
      <span className={s.tipo}>{rotulo}</span>
      <span className={s.capability}>
        {/* aim-92c: a linha de tool mostra a tool como T5 e T7 — o Display name do catálogo de
            hoje em destaque e o par `<apim_id>/<tool>` embaixo, com o detalhe na linha do
            destaque. A2A e credencial seguem como texto: não são tool. */}
        {isToolChange(mudanca.kind) ? (
          <ToolName
            displayName={mudanca.tool_display_name}
            technicalName={mudanca.capability}
            trailing={detalhe ? <span className={s.detalhe}>· {detalhe}</span> : null}
          />
        ) : (
          <>
            {mudanca.capability}
            {detalhe ? <span className={s.detalhe}> · {detalhe}</span> : null}
          </>
        )}
      </span>
      <Badge tone={severidade.tone}>{severidade.rotulo}</Badge>
      <span className={s.evidenceClasses} aria-label={t('approvals.evidence.classes')}>
        <span>{t('approvals.evidence.classes')}: </span>
        {recorded ? (
          <>
            {recorded.classes.map((value) => <Badge key={value} tone="neutral">{value}</Badge>)}
            {recorded.unknown ? <Badge tone="warning">{t('approvals.evidence.unknownClasses')}</Badge> : null}
            {evidence && savedRuleRequires(evidence, recorded) ? (
              <Badge tone="warning">{t('approvals.evidence.ruleRequires')}</Badge>
            ) : null}
          </>
        ) : <span className={s.muted}>{t('approvals.evidence.classesUnavailable')}</span>}
      </span>
      {destaque ? (
        <InfoTip
          text={notCatalogedTip()}
          label={t('approvals.diff.notCataloged.tipLabel')}
          size="xs"
        />
      ) : null}
    </div>
  );
}
